import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { pageViewEvent } from '../hooks/useAnalytics';

/**
 * Hook to track page views in various analytics platforms
 * @param {Object} options - Configuration options
 * @param {Function} options.callback - Optional callback function after tracking
 */
export const useTrackPageView = ({ callback } = {}) => {
  const location = useLocation();
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    try {
      // Track the page view using our analytics function
      trackPageView({
        path: location.pathname,
        search: location.search,
        title: document.title,
        url: window.location.href,
        referrer: document.referrer
      });
      
      // Execute callback if provided
      if (typeof callback === 'function') {
        callback(location);
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }, [location, callback]);
};

/**
 * Function to manually track a page view
 * @param {Object} pageData - Page data to track
 */
export const trackPageView = (pageData = {}) => {
  if (typeof window === 'undefined') return;
  
  try {
    // Prepare the page data with defaults
    const data = {
      path: pageData.path || window.location.pathname,
      search: pageData.search || window.location.search,
      title: pageData.title || document.title,
      url: pageData.url || window.location.href,
      pageType: pageData.pageType || 'page',
      ...pageData
    };
    
    // Use our centralized analytics tracking function
    pageViewEvent(data);
    
    // Track in Google Analytics
    if (window.gtag) {
      window.gtag('config', window.GA_TRACKING_ID || 'UA-XXXXX-X', {
        page_path: data.path,
        page_location: data.url,
        page_title: data.title,
      });
    }
    
    // Log tracking if in development environment
    if (process.env.NODE_ENV !== 'production') {
      console.log('Page view tracked:', {
        path: data.path,
        title: data.title,
        type: data.pageType
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error manually tracking page view:', error);
    return false;
  }
};

/**
 * Create a tracking event for specific user actions
 * @param {string} eventName - Name of the event 
 * @param {Object} eventData - Data related to the event
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (typeof window === 'undefined') return;
  
  try {
    // Default event data
    const data = {
      event: eventName,
      timestamp: new Date().toISOString(),
      page: {
        path: window.location.pathname,
        title: document.title
      },
      ...eventData
    };
    
    // Push to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push(data);
    }
    
    // Push to Google Analytics if available
    if (window.gtag) {
      window.gtag('event', eventName, eventData);
    }
    
    return true;
  } catch (error) {
    console.error(`Error tracking event ${eventName}:`, error);
    return false;
  }
};

export default {
  useTrackPageView,
  trackPageView,
  trackEvent
};